import Cta from '@atoms/Cta/Cta';
import Image from '@atoms/Image/Image';
import Text from '@atoms/Text/Text';
import { IQuoteImageBlock } from '@models/IQuoteImageBlock';
import { replaceQuotes } from '@utility/format/replaceQuotes';
import classNames from 'classnames';

const QuoteBlockContent = ({
  site,
  image,
  text,
  author,
  authorPosition,
  link,
  useWidescreenStyles,
}: IQuoteImageBlock & {
  readonly useWidescreenStyles: boolean;
}) => (
  <div
    className={classNames(
      {
        'lg:tw-w-[calc(100%+32px)] xl:tw-flex-row': useWidescreenStyles,
      },
      {
        'lg:tw-col-span-10 xl:tw-gap-x-28': image && useWidescreenStyles,
      },
      {
        'lg:tw-col-span-8': !image && useWidescreenStyles,
      },
      {
        'tw-pt-6 lg:tw-col-span-12 xl:tw-flex-row': !useWidescreenStyles,
      },
      'tw-col-span-4 tw-flex tw-gap-md'
    )}
  >
    {useWidescreenStyles && image && (
      <Image
        classes="max-xl:tw-hidden tw-object-cover tw-shrink-0 author-block tw-w-[192px] tw-space-y-md"
        imageClasses="tw-object-cover"
        width={192}
        aspectRatio={1}
        image={image}
      />
    )}
    <div className="tw-space-y-md">
      <p className={'tw-font-serif tw-text-3xl'}>{replaceQuotes(text)}</p>
      <div className="tw-flex tw-gap-4">
        {image && (
          <Image
            classes={classNames(
              'tw-object-cover author-block tw-h-[80px] tw-w-[80px]',
              {
                'xl:tw-hidden ': useWidescreenStyles,
              }
            )}
            imageClasses="tw-object-cover"
            image={image}
            aspectRatio={1}
            width={80}
          />
        )}
        <div className="tw-flex tw-flex-col tw-justify-center">
          {useWidescreenStyles ? (
            <>
              <Text variant="body-18" className="tw-text-grey-100">
                {author}
              </Text>
              <Text variant="body-small" className="tw-text-grey-70">
                {authorPosition}
              </Text>
            </>
          ) : (
            <>
              <Text variant="body-14-semibold" className="tw-text-grey-80">
                {author}
              </Text>
              <Text variant="body-small" className="tw-text-grey-80">
                {authorPosition}
              </Text>
            </>
          )}
        </div>
      </div>
      {link?.url && link?.text && (
        <Cta
          site={site}
          linkIcon="arrow-right"
          url={link.url}
          text={link.text}
          target={link.target}
        />
      )}
    </div>
  </div>
);

const QuoteImageBlock = ({ greyBackground, ...props }: IQuoteImageBlock) => {
  const sharedClasses =
    'tw-grid-cols-4 tw-gap-xs md:tw-gap-md lg:tw-grid-cols-12';
  return (
    <>
      {/* Quote Block layout on a regular page */}
      <section className={greyBackground ? 'tw-bg-grey-10' : 'tw-bg-white'}>
        <div
          className={classNames(
            'tw-container tw-grid tw-px-4 tw-py-12 group-[.rich-text--article]/article:tw-hidden',
            sharedClasses
          )}
        >
          <QuoteBlockContent {...props} useWidescreenStyles={true} />
        </div>
      </section>
      {/* Quote Block layout on article page */}
      <section
        className={classNames(
          'tw--mb-12 tw-hidden group-[.rich-text--article]/article:tw-grid xl:tw--mb-[80px]',
          sharedClasses
        )}
      >
        <QuoteBlockContent {...props} useWidescreenStyles={false} />
      </section>
    </>
  );
};

export default QuoteImageBlock;
